import { useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/Button";
import { useIsMobile } from "../../../../helpers/ui";
import { colors } from "../../../../styles";
import { Card } from "../Account/components";
import { useStoreActions, useStoreState } from "../../../../store";
import { useNavigate } from "react-router-dom";
import T from "../../../../components/Typography";
import { formatCurrency } from "../../../../helpers/currency";
import { BASE64_IMAGE } from "../../../../helpers/image";
import { ReactComponent as BankIcon } from "../../../../assets/icons/adminDashboard/bank.svg";
import { SectionFlexColumn } from "../../../../components/General";
import TransfersService from "../../../../api/services/TransfersService";
import { QUERY_ACH_TRANSFERS_HISTORY } from "../../../../api/queryKeys";
import { employeeTransfersErrorPath } from "../../../../routes/paths";

const ReviewTransfer = () => {
  const transferState = useStoreState((state) => state.transfer);
  const transferActions = useStoreActions((actions) => actions.transfer);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  if (!transferState.amount) {
    navigate(-1);
    return null;
  }

  if (!transferState.externalAccount) {
    navigate(-2);
    return null;
  }

  const externalAccount = transferState.externalAccount;

  const handleConfirm = async () => {
    const response = await TransfersService.createACHTransfer({
      amount: transferState.amount as string,
      direction: transferState.direction,
      externalAccountId: externalAccount.id,
    });

    if (response?.data.id) {
      queryClient.invalidateQueries([QUERY_ACH_TRANSFERS_HISTORY]);
      transferActions.setCreatedTransferId(response.data.id);
      transferActions.setCurrentStep(transferState.nextStep);
      return;
    }
    if (response?.data?.error) {
      navigate(employeeTransfersErrorPath);
      return;
    }
  };

  return (
    <Card
      hideStyleOnMobile
      sx={{
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        ...(isMobile && { display: "flex", flexDirection: "column" }),
      }}
    >
      <T variant="headingSm">Moving</T>
      <T variant="headingLg" sx={{ fontSize: "64px", fontWeight: "bold" }}>
        {formatCurrency(transferState.amount, 2)}
      </T>
      <T variant="headingSm" sx={{ marginBottom: "16px" }}>
        {transferState.direction === "add_money" ? "from" : "to"}
      </T>
      <div style={{ marginBottom: "16px" }}>
        {externalAccount.plaid_institution_logo ? (
          <img
            src={`${BASE64_IMAGE}${externalAccount.plaid_institution_logo}`}
            alt="bank logo"
            style={{
              width: "80px",
              height: "80px",
              border: `1px solid ${colors.gray_400}`,
              padding: "2px",
              borderRadius: "40px",
            }}
          />
        ) : (
          <BankIcon
            style={{
              width: "80px",
              height: "80px",
              border: `1px solid ${colors.gray_400}`,
              padding: "2px",
              borderRadius: "40px",
            }}
          />
        )}
      </div>
      <SectionFlexColumn sx={{ marginBottom: "32px" }}>
        <T variant="mdMed">{externalAccount.nickname}</T>
        <T variant="mdMed" color={colors.gray_400}>
          {externalAccount.plaid_institution_name}
        </T>
      </SectionFlexColumn>
      <div
        style={{
          borderTop: `1px solid ${colors.gray_200}`,
          width: "100%",
          paddingTop: "32px",
        }}
      >
        <T variant="lgMed" sx={{ marginBottom: "12px" }}>
          Good to Know
        </T>
        <T variant="mdReg" color={colors.gray_600}>
          Transfers are free & typically take 2-3 days
        </T>
        <Button noMargin sx={{ marginTop: "32px" }} onClick={handleConfirm}>
          Confirm
        </Button>
      </div>
    </Card>
  );
};

export default ReviewTransfer;
